import classNames from "classnames";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  useGetSupportMailboxQuery,
  useGetSupportSignaturesQuery,
  usePreviewNewSupportEmailMutation,
  useSendNewSupportEmailMutation,
  useTestNewSupportEmailMutation,
} from "../../slices/SupportSlice";
import {
  modalCancel,
  modalPrimary,
  PortalModal,
} from "../clientPortal/PortalModal";
import {
  AddressChips,
  CopiesFields,
  CopiesLinks,
  CopiesState,
  NO_COPIES,
} from "./CopiesFields";
import { EmailPreviewModal } from "./EmailPreviewModal";
import {
  browserTimeZone,
  cleanCopies,
  copiesNote,
  MAX_SUBJECT,
  newEmailProblem,
  newEmailResultText,
  parseRecipients,
} from "./schedule.utils";
import { ScheduleSendButton } from "./ScheduleSendButton";
import { SendTestBox } from "./SendTestBox";
import { SignaturePicker } from "./SignaturesCard";
import { errorText, initialSignatureChoice, MAX_REPLY } from "./support.utils";

const INPUT =
  "h-10 w-full rounded-lg border border-[#DCE2EA] bg-white px-3 text-[14px] text-[#1F2433] outline-none focus:border-[#6B4EFF]";
const LABEL = "mb-1 block text-[12.5px] font-bold text-[#3A4256]";

/**
 * "New email" on the Help desk (owner 2026-11-03): an email from support@ to one or more people, sent now or
 * scheduled. Each address gets its own email and its own case (they never see each other's address), so their
 * replies land on that case like any other.
 */
export const NewEmailModal = ({
  onClose,
  initialTo = "",
}: {
  onClose: () => void;
  initialTo?: string;
}) => {
  const navigate = useNavigate();
  const [to, setTo] = useState(initialTo);
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [tried, setTried] = useState(false);
  // Cc / Bcc (owner 2026-11-03): behind small links, like mail apps.
  const [copies, setCopies] = useState<CopiesState>(NO_COPIES);
  const [testing, setTesting] = useState(false);
  const [signatureChoice, setSignatureChoice] = useState<string | null>(null);
  const { data: signatures } = useGetSupportSignaturesQuery();
  const { data: mailbox } = useGetSupportMailboxQuery();
  const signature =
    signatureChoice ?? initialSignatureChoice(signatures?.signatures);
  const signatureToSend = signatures?.available ? signature : undefined;
  const [sendNew, { isLoading: isSending }] = useSendNewSupportEmailMutation();
  const [
    runPreview,
    { isLoading: isPreviewing, data: preview, reset: clearPreview },
  ] = usePreviewNewSupportEmailMutation();
  const [sendTest] = useTestNewSupportEmailMutation();

  const { addresses } = parseRecipients(to);
  const cleaned = cleanCopies(addresses, copies.cc, copies.bcc);
  const note = copiesNote(addresses.length, cleaned.cc, cleaned.bcc);
  const problem = newEmailProblem({ to, subject, text }) || cleaned.problem;
  const request = () => ({
    cc: cleaned.cc,
    bcc: cleaned.bcc,
    to: addresses,
    subject: subject.trim(),
    text,
    signature: signatureToSend,
  });

  const submit = async (at?: Date) => {
    setTried(true);
    if (problem) return;
    try {
      const result = await sendNew({
        ...request(),
        ...(at
          ? { sendAt: at.toISOString(), timeZone: browserTimeZone() }
          : {}),
      }).unwrap();
      const message = newEmailResultText(result.results, result.sendAt);
      toast(message.text, { type: message.ok ? "success" : "warning" });
      clearPreview();
      onClose();
      if (result.results.length === 1)
        navigate(`/support/${result.results[0].ticketId}`);
    } catch (e) {
      toast(errorText(e, "The email wasn't sent. Please try again."), {
        type: "error",
      });
    }
  };

  const doPreview = async () => {
    setTried(true);
    if (problem) return;
    try {
      await runPreview(request()).unwrap();
    } catch (e) {
      toast(errorText(e, "The preview couldn't be made. Please try again."), {
        type: "error",
      });
    }
  };

  const showProblem = tried && problem;
  return (
    <>
      <PortalModal
        title={"New email"}
        subtitle={
          "From support@procharted.com. Each address gets its own email and its own case."
        }
        // Esc in the test box / preview closes only that one, not this window with the typed email.
        onClose={() => {
          if (!testing && !preview) onClose();
        }}
        width={680}
        footer={
          <div
            className={"flex w-full flex-wrap items-center justify-end gap-2"}
            data-testid={"new-email-footer"}>
            <button
              type={"button"}
              disabled={isPreviewing}
              onClick={() => void doPreview()}
              className={modalCancel}
              data-testid={"new-email-preview"}>
              {isPreviewing ? "Opening…" : "Preview"}
            </button>
            <button
              type={"button"}
              onClick={() => {
                setTried(true);
                if (!problem) setTesting(true);
              }}
              className={modalCancel}
              data-testid={"new-email-test"}>
              Send test
            </button>
            <span className={"flex-1"} />
            <ScheduleSendButton
              disabled={isSending || (tried && !!problem)}
              title={showProblem || "Send it later, at a time you pick"}
              onSchedule={(at) => void submit(at)}
              testId={"new-email-schedule"}
            />
            <button
              type={"button"}
              disabled={isSending}
              onClick={() => void submit()}
              className={modalPrimary}
              data-testid={"new-email-send"}>
              {isSending ? "Sending…" : "Send now"}
            </button>
          </div>
        }>
        <div>
          <div className={"mb-1 flex items-center justify-between"}>
            <label htmlFor={"new-email-to"} className={LABEL + " mb-0"}>
              To
            </label>
            <CopiesLinks value={copies} onChange={setCopies} />
          </div>
          <input
            id={"new-email-to"}
            autoFocus
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder={"name@firm.com, another@firm.com"}
            className={INPUT}
          />
          <AddressChips
            text={to}
            hint={
              "Separate addresses with commas (up to 10). Each gets their own email."
            }
          />
        </div>
        {(copies.showCc || copies.showBcc) && (
          <div className={"flex flex-col gap-2"}>
            <CopiesFields
              value={copies}
              onChange={setCopies}
              idPrefix={"new-email"}
            />
            {note && (
              <div
                className={
                  "rounded-lg bg-[#F4F1FF] px-3 py-2 text-[12.5px] text-[#4B32D6]"
                }
                data-testid={"copies-note"}>
                {note}
              </div>
            )}
          </div>
        )}
        <div>
          <label htmlFor={"new-email-subject"} className={LABEL}>
            Subject
          </label>
          <input
            id={"new-email-subject"}
            value={subject}
            maxLength={MAX_SUBJECT}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={"e.g. Your ProCharted account"}
            className={INPUT}
          />
        </div>
        <div
          className={
            "rounded-xl border-[1.5px] border-[#DCE2EA] bg-white px-4 py-3"
          }>
          <label
            htmlFor={"new-email-text"}
            className={"mb-1.5 block text-[12.5px] text-[#7A7F8C]"}>
            <b className={"text-[#1F2433]"}>Message</b>
          </label>
          <textarea
            id={"new-email-text"}
            value={text}
            maxLength={MAX_REPLY}
            rows={Math.min(14, Math.max(7, text.split("\n").length + 1))}
            onChange={(e) => setText(e.target.value)}
            placeholder={"Hi …"}
            className={
              "w-full resize-y border-0 bg-transparent text-[14px] leading-relaxed text-[#1F2433] outline-none"
            }
          />
          <SignaturePicker value={signature} onChange={setSignatureChoice} />
        </div>
        <div
          className={classNames(
            "text-[12.5px]",
            showProblem ? "text-[#B45309]" : "text-[#5D6475]",
          )}>
          {showProblem ||
            (mailbox?.connected
              ? "Goes out from support@ and shows in its Sent folder. Their replies come back to the Help desk."
              : "support@ isn't connected, so it goes out through ProCharted's email service. Replies reach the Help desk once support@ is connected.")}
        </div>
      </PortalModal>
      {testing && (
        <SendTestBox
          ticketId={""}
          text={text}
          then={"waiting"}
          signature={signatureToSend}
          nobody={"Nobody else"}
          send={(testTo) => sendTest({ ...request(), testTo }).unwrap()}
          onClose={() => setTesting(false)}
        />
      )}
      {preview && (
        <EmailPreviewModal
          preview={preview}
          onClose={clearPreview}
          sendLabel={isSending ? "Sending…" : "Send now"}
          sendDisabled={!!problem || isSending}
          onSend={() => void submit()}
          note={
            addresses.length > 1
              ? `Each of the ${addresses.length} people gets this email on its own. Nothing has been sent.`
              : "This is exactly what they will get. Nothing has been sent."
          }
        />
      )}
    </>
  );
};
